<template>
  <v-content>
    <v-container my-10>
      <v-layout align-center justify-center>
        <v-progress-circular
          :size="55"
          :width="4"
          class="mt-12"
          color="primary"
          indeterminate
          v-if="loading || getDocumentLoadingStatus || getItemLoadingStatus"
        />
        <v-form ref="form" v-model="valid" lazy-validation v-else>
          <v-card color="primary" dark max-width="650px" v-if="!loading" tile>
            <v-card-title>
              <span class="headline" v-if="isEditView"
                >{{ editedItem.model.brand_name }}
                {{ editedItem.model.model }} #{{ editedItem.slot_number }}</span
              >
              <span class="headline" v-else>{{
                $t("builder.boats.addEdit.newBoat")
              }}</span>
            </v-card-title>
          </v-card>

          <v-tabs fixed-tabs background-color="primary" dark v-model="tab">
            <v-tab>
              <v-icon class="mr-2">mdi-information</v-icon>
              {{ $t("builder.boats.addEdit.boatInformation") }}
            </v-tab>
            <v-tab
              :disabled="!editedItem.can_edit_paper_consents"
              v-if="editedItem.can_edit_paper_consents"
            >
              <v-icon class="mr-2">mdi-database-cog</v-icon>
              {{ $t("builder.boats.addEdit.permissions") }}
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-card max-width="650px" v-if="!loading">
                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <v-text-field
                          v-model="editedItem.slot_number"
                          color="primary"
                          :hint="
                            getRule('slot').required
                              ? $t('builder.boats.addEdit.required')
                              : ''
                          "
                          :label="getRule('slot').label"
                          type="number"
                          :rules="getRule('slot').handler"
                          :disabled="editableFields('slot_number')"
                          persistent-hint
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <v-text-field
                          v-model="editedItem.ce_number"
                          color="primary"
                          :hint="
                            getRule('hin').required
                              ? $t('builder.boats.addEdit.required')
                              : ''
                          "
                          :label="getRule('hin').label"
                          persistent-hint
                          :rules="getRule('hin').handler"
                          :disabled="editableFields('ce_number')"
                          autocomplete="off"
                        />
                      </v-col>
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <device-selector
                          :device-serial.sync="editedItem.device_serial"
                          :initial-data="editedItem.device"
                          :rules="getRule('sentinelSN').handler"
                          :label="getRule('sentinelSN').label"
                          :hint="
                            getRule('sentinelSN').required
                              ? $t('builder.boats.addEdit.required')
                              : ''
                          "
                          :disabled="editableFields('device_serial')"
                        />
                      </v-col>

                      <!-- MODEL -->
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <model-selector
                          :initial-data="editedItem.model"
                          :model-uuid.sync="editedItem.model_uuid"
                          :is-warning-dialog-available="isEditView"
                          :rules="getRule('model').handler"
                          :label="getRule('model').label"
                          :hint="
                            getRule('model').required
                              ? $t('builder.boats.addEdit.required')
                              : ''
                          "
                          :disabled="editableFields('model')"
                          @reload-boat-items="loadNewBoatItems"
                        />
                      </v-col>
                      <div style="width: 100%" v-if="model !== null">
                        <!-- BOAT ITEMS -->
                        <v-col
                          cols="12"
                          lg="12"
                          md="12"
                          sm="12"
                          style="padding-bottom: 0px"
                        >
                          <v-row class="pl-3">
                            <h3 class="subtitle-1 mt-1">
                              {{ $t("builder.boats.addEdit.parts") }}
                            </h3>
                            <v-spacer></v-spacer>
                            <licence-tooltip
                              :feature="createNewPartsHandler"
                              :disabled="createNewPartsHandler.disabled"
                              :enabled="createNewPartsHandler.enabled"
                              float-direction="right"
                            >
                              <template
                                v-slot:action="{ attrs, disabled, enabled }"
                              >
                                <v-btn
                                  color="primary"
                                  text
                                  @click="openItemDialog()"
                                  v-bind="attrs"
                                  :disabled="disabled"
                                >
                                  <v-icon small class="mr-1" v-if="enabled">
                                    mdi-star-outline </v-icon
                                  >{{
                                    $t("builder.boats.addEdit.createPart")
                                  }}</v-btn
                                >
                              </template>
                            </licence-tooltip>
                            <template>
                              <v-btn
                                text
                                color="primary"
                                dark
                                class="float-right"
                                @click="importCSV"
                                v-if="showImport"
                              >
                                {{ $t("builder.boats.addEdit.import") }}
                              </v-btn>
                              <input
                                ref="uploader"
                                class="d-none"
                                type="file"
                                accept=".csv"
                                @change="onFileChanged"
                                id="csvFileImport"
                              />
                            </template>
                          </v-row>
                        </v-col>
                        <v-col
                          cols="12"
                          lg="12"
                          md="12"
                          sm="12"
                          style="padding-top: 4px"
                        >
                          <keep-alive>
                            <boat-item-list
                              :missing-categories="
                                editedItem.profile_missing_categories
                              "
                              :initial-data="initialBoatItemsData"
                              :boat-items.sync="boatItems"
                              :deleted-boat-items.sync="deletedBoatItems"
                              :disable-selector="
                                editableFields('boat_items_to_create')
                              "
                              :disable-adding="
                                editableFields('boat_items_to_update')
                              "
                              :disable-deleting="
                                editableFields('boat_items_to_delete')
                              "
                              ref="boatItemsRef"
                              :key="reloadBoatList"
                            ></boat-item-list>
                          </keep-alive>
                        </v-col>

                        <!-- DOCUMENTS -->
                        <!-- SERIAL DOCUMENTS -->
                        <v-col cols="1" lg="12" md="12" sm="12">
                          <h3 class="subtitle-1">
                            {{ $t("builder.boats.addEdit.standardDocuments") }}
                          </h3>
                          <v-list
                            :key="document.uuid"
                            v-for="document in serialDocuments"
                          >
                            <document-component
                              :document="document"
                              :readonly="false"
                              @delete-document="deleteSerialDocuments($event)"
                            />
                          </v-list>
                        </v-col>
                        <!-- CUSTOM DOCUMENTS -->
                        <v-col
                          cols="12"
                          lg="12"
                          md="12"
                          sm="12"
                          style="padding-bottom: 0px"
                        >
                          <v-row class="pl-3">
                            <h3 class="subtitle-1 mt-1">
                              {{ $t("builder.boats.addEdit.customDocuments") }}
                            </h3>
                            <v-spacer></v-spacer>

                            <licence-tooltip
                              :feature="createNewDocumentsHandler"
                              :disabled="createNewDocumentsHandler.disabled"
                              :enabled="createNewDocumentsHandler.enabled"
                              float-direction="right"
                            >
                              <template
                                v-slot:action="{ attrs, disabled, enabled }"
                              >
                                <v-btn
                                  class="float-right"
                                  color="primary"
                                  text
                                  @click="openDocumentDialog()"
                                  v-bind="attrs"
                                  :disabled="disabled"
                                >
                                  <v-icon small class="mr-1" v-if="enabled">
                                    mdi-star-outline </v-icon
                                  >{{
                                    $t("builder.boats.addEdit.createDocument")
                                  }}</v-btn
                                >
                              </template>
                            </licence-tooltip>
                          </v-row>
                        </v-col>
                        <v-col cols="12" lg="12" md="12" sm="12" class="mt-1">
                          <document-picker
                            :boat-items="boatItems"
                            :unavailable-documents="serialDocuments"
                            :documents="customDocuments"
                            :readonly="editableFields('crm_document_uuids')"
                            :disabled="editableFields('crm_document_uuids')"
                            @add:document="addCustomDocuments($event)"
                            @delete:document="deleteCustomDocuments($event)"
                            :key="forceUpdateCustomDocuments"
                            feature="add-document-on-boat"
                          />
                        </v-col>
                      </div>

                      <!-- DEALERS -->
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <dealer-selector
                          :dealer-type="'SALES'"
                          :initial-dealer-uuid="editedItem.sales_dealer_uuid"
                          :initial-dealer="editedItem.sales_dealer"
                          :label="$t('builder.boats.addEdit.salesPartner')"
                          :disabled="editableFields('sales_dealer')"
                          :key="forceUpdateSalesDealer"
                          @change="handleDealerChange($event, 'SALES')"
                        />
                      </v-col>

                      <v-col cols="12" lg="12" md="12" sm="12">
                        <dealer-selector
                          :dealer-type="'RESPONSIVE'"
                          :initial-dealer-uuid="
                            editedItem.responsive_dealer_uuid
                          "
                          :initial-dealer="editedItem.responsive_dealer"
                          :label="
                            $t(
                              'builder.boats.addEdit.servicingPartnerCustomerService'
                            )
                          "
                          :load-dealers-external="
                            canUserChangeResponsivePartner
                          "
                          :disabled="
                            editableFields('responsive_dealer') &&
                            !canUserChangeResponsivePartner
                          "
                          :key="forceUpdateResponsiveDealer"
                          @change="handleDealerChange($event, 'RESPONSIVE')"
                        />
                      </v-col>

                      <!-- DATEPICKER -->
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <date-picker
                          clearable
                          :provider-date.sync="editedItem.delivery_date"
                          v-bind:date-label="
                            $t(
                              'builder.boats.addEdit.deliveryDateToPartnerOptional'
                            )
                          "
                          :disabled="editableFields('delivery_date')"
                        />
                      </v-col>
                      <v-col cols="12" lg="12" md="12" sm="12">
                        <date-picker
                          clearable
                          :provider-date.sync="
                            editedItem.delivery_date_to_customer
                          "
                          v-bind:date-label="
                            $t('builder.boats.addEdit.deliveryDateToCustomer')
                          "
                          :disabled="
                            editableFields('delivery_date_to_customer')
                          "
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="12"
                        md="12"
                        lg="12"
                        v-if="canSetCrmBoatTelematicsProfile"
                      >
                        <v-card-actions class="mt-4 pt-0 pl-0 ml-0">
                          <v-btn
                            text
                            class="mt-0 pt-0 pl-0 ml-0"
                            color="primary"
                            @click="showAdvanceSection = !showAdvanceSection"
                          >
                            <v-icon>{{
                              showAdvanceSection
                                ? "mdi-chevron-up"
                                : "mdi-chevron-down"
                            }}</v-icon>
                            {{ $t("builder.boats.addEdit.advanced") }}
                          </v-btn>
                        </v-card-actions>
                        <v-expand-transition class="ml-0 pl-0">
                          <div v-show="showAdvanceSection">
                            <v-card-title
                              class="subtitle-1 mt-4 pt-0 pl-0 ml-0 pb-0"
                            >
                              {{
                                $t("builder.boats.addEdit.telematicsProfile")
                              }}
                            </v-card-title>
                            <v-card-text class="mt-0 pt-0 pl-0 ml-0 pb-0 mb-0">
                              <profile-selector
                                v-if="model != null"
                                :model_selected_profile_uuid="
                                  model.selected_profile != null
                                    ? model.selected_profile.uuid
                                    : null
                                "
                                :disabled="
                                  editedItem.retrofit_status == 'CONFIRMED'
                                "
                                :selected_profile_uuid.sync="
                                  editedItem.selected_profile_uuid
                                "
                                :selected_brand_uuid="model.brand.uuid"
                                :initial_data="
                                  editedItem.selected_profile || null
                                "
                                :sort="true"
                                :boat_specific="true"
                                :key="profileSelectorKey"
                              />
                            </v-card-text>
                          </div>
                        </v-expand-transition>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="close()" color="primary" text>{{
                    $t("builder.boats.addEdit.cancel")
                  }}</v-btn>
                  <v-btn @click="checkForDealerChange" color="primary" text>{{
                    $t("builder.boats.addEdit.save")
                  }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-tab-item>
            <v-tab-item v-if="editedItem.can_edit_paper_consents">
              <v-card max-width="650px" width="650px" v-if="!loading">
                <v-container>
                  <v-card-title class="subtitle-1">
                    {{
                      $t(
                        "builder.boats.addEdit.selectPartnersWhoHaveBeenGrantedAPermissionForSharingInPaperFormThisActionIsAvailableOnlyTheUserRegistersTheBoatInHisAppAfterThatPermissionsApply"
                      )
                    }}</v-card-title
                  >
                  <v-row>
                    <v-col cols="12" lg="12" md="12" sm="12">
                      <v-list
                        subheader
                        two-line
                        flat
                        v-for="(consent, index) in paperConsents"
                        :key="index"
                      >
                        <v-list-item-title class="ml-4"
                          ><div v-if="consent.crm_partner !== null">
                            <span
                              v-if="consent.crm_partner.domain === 'RESPONSIVE'"
                              >{{
                                $t("builder.boats.addEdit.responsivePartner")
                              }}
                              <span class="font-weight-bold">{{
                                consent.crm_partner.name
                              }}</span>
                            </span>
                            <span v-if="consent.crm_partner.domain === 'SALES'"
                              >{{ $t("builder.boats.addEdit.salesPartner:") }}
                              <span class="font-weight-bold">{{
                                consent.crm_partner.name
                              }}</span>
                            </span>
                          </div>
                        </v-list-item-title>
                        <v-list-item>
                          <v-list-item-action>
                            <v-checkbox
                              v-model="consent.active"
                              color="primary"
                            ></v-checkbox>
                          </v-list-item-action>

                          <v-list-item-content>
                            <v-list-item-title>{{
                              consent.name
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn @click="closePermissions()" color="primary" text>{{
                    $t("builder.boats.addEdit.cancel")
                  }}</v-btn>
                  <v-btn
                    @click="openPaperConsentsDialog"
                    color="primary"
                    text
                    >{{ $t("builder.boats.addEdit.save") }}</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-form>
        <inline-item-dialog />
        <inline-document-dialog />
        <upload-file-dialog
          v-if="uploadDialog.active"
          :is-edit-view="isEditView"
          :dialog="uploadDialog"
          :boat-items="boatItems"
          @close="handleCloseUploadFileDialog"
        ></upload-file-dialog>
      </v-layout>
      <unsaved-content-warning
        :dialog="unsavedContent"
        @redirect="redirectRoute"
      ></unsaved-content-warning>
      <warning-dialog
        :dialog="paperConsentsDialog.active"
        :text="paperConsentsDialog.text"
        :title="paperConsentsDialog.title"
        @agree="handlePaperConsentsDialogOutput"
        :confirm="$t('builder.boats.addEdit.save')"
        :reject="$t('builder.boats.addEdit.cancel')"
        v-show="false"
      />
      <warning-dialog
        :dialog="confirmProfileChangeDialog.active"
        :text="confirmProfileChangeDialog.text"
        :title="confirmProfileChangeDialog.title"
        @agree="handleConfirmProfileChangeOutput"
        :confirm="$t('builder.boats.addEdit.applyChange')"
        :reject="$t('builder.boats.addEdit.cancel')"
        v-show="false"
      />
      <warning-dialog
        :dialog="dealerChangeDialog.active"
        :title="$t('builder.boats.addEdit.confirmChangeOfPartner')"
        @agree="handleDealerChangeDialog"
        :confirm="$t('builder.boats.addEdit.confirmChanges')"
        :reject="$t('builder.boats.addEdit.cancel')"
        v-show="dealerChangeDialog.active"
      >
        <template v-slot:text>
          <v-card flat class="mt-0 pt-0 pl-2" v-if="dealerChangeDialog.active">
            <v-card-text
              class="pt-0"
              v-if="
                dealerChangeDialog.responsive.active ||
                dealerChangeDialog.sales.active
              "
            >
              {{
                $t(
                  "builder.boats.addEdit.whenChangingPartnersAnyConsentsForDataSharingWithThePreviousPartnerAreResetAndTheUserMayBePromptedInTheMobileAppToGrantNewNecessaryDataSharingConsentsToTheNewPartner",
                  {
                    oldPartners: computeInitialDealerChangeString(),
                    newPartners: computeCurrentDealerChangeString(),
                  }
                )
              }}
            </v-card-text>

            <v-card-text
              class="pt-0"
              v-if="
                dealerChangeDialog.items.length !== 0 ||
                dealerChangeDialog.documents.length !== 0
              "
            >
              {{
                $t(
                  "builder.boats.addEdit.additionallyPartsAndDocumentsCreatedByThePreviousPartnerAndAssignedToThisBoatWillBeRemoved",
                  {
                    oldPartners: computeInitialDealerChangeString(),
                  }
                )
              }}
            </v-card-text>

            <div v-if="dealerChangeDialog.items.length !== 0">
              <v-card-text class="pt-0 pb-0">
                {{
                  $t("builder.boats.addEdit.thesePartsWillBeRemovedFromTheBoat")
                }}
              </v-card-text>
              <v-card-subtitle
                class="pt-0"
                v-for="(item, index) in dealerChangeDialog.items"
                :key="index"
              >
                • {{ item.name }}</v-card-subtitle
              >
            </div>

            <div v-if="dealerChangeDialog.documents.length !== 0">
              <v-card-text>
                {{
                  $t(
                    "builder.boats.addEdit.theseDocumentsWillBeRemovedFromTheBoat"
                  )
                }}
              </v-card-text>
              <v-card-subtitle
                class="pt-0"
                v-for="(documents, index) in dealerChangeDialog.documents"
                :key="index"
              >
                • {{ documents.title }}</v-card-subtitle
              >
            </div>

            <v-card-text class="mb-1">
              <b>{{ $t("builder.boats.addEdit.thisActionCannotBeUndone") }}</b>
            </v-card-text>
          </v-card>
        </template>
      </warning-dialog>
    </v-container>
  </v-content>
</template>

<script>
import api from "@/utils/api.js";
import DatePicker from "@/components/pickers/DatePicker";
import DocumentPicker from "@/components/pickers/DocumentPicker";
import DocumentComponent from "@/components/documents/DocumentComponent";
import DeviceSelector from "@/components/selectors/DeviceSelector";
import ModelSelector from "@/components/selectors/ModelSelector";
import DealerSelector from "@/components/selectors/DealerSelector";
import snackbar from "@/utils/snackbar.js";
import InlineItemDialog from "@/components/dialogs/InlineItemDialog";
import InlineDocumentDialog from "@/components/dialogs/InlineDocumentDialog";
import UploadFileDialog from "@/components/dialogs/UploadFileDialog";
import { mapGetters } from "vuex";
import restrictions from "@/utils/restrictions";
import UnsavedContentWarning from "@/components/dialogs/UnsavedContentWarningDialog";
import BoatItemList from "@/components/boat-items/BoatItemList";
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import LicenceTooltip from "@/components/general/LicenceTooltip";
import ProfileSelector from "@/components/selectors/ProfileSelector";

export default {
  components: {
    ProfileSelector,
    DealerSelector,
    ModelSelector,
    DeviceSelector,
    DatePicker,
    DocumentPicker,
    DocumentComponent,
    InlineItemDialog,
    InlineDocumentDialog,
    UploadFileDialog,
    UnsavedContentWarning,
    BoatItemList,
    WarningDialog,
    LicenceTooltip,
  },
  data: () => ({
    tab: null,
    loading: true,
    isBoatEdited: false,
    isEditView: false,
    valid: true,
    model: null,
    serialDocuments: [],
    customDocuments: [],
    unavailableDocuments: [],
    boatItems: [],
    deletedBoatItems: [],
    initialBoatItemsData: {},
    reloadBoatList: 0,
    isSelecting: false,
    forceUpdateCustomDocuments: 0,
    forceUpdateResponsiveDealer: 0,
    forceUpdateSalesDealer: 0,
    unsavedContent: { active: false, route: null },
    paperConsentsDialog: {
      active: false,
      title: "",
      text: "",
    },
    showAdvanceSection: false,
    profileSelectorKey: 0,
    confirmProfileChangeDialog: {
      active: false,
      title: "",
      text: "",
    },
    uploadDialog: {
      file: null,
      active: false,
      title: "",
      text: "",
    },
    dealerChangeDialog: {
      active: false,
      title: "",
      documents: [],
      items: [],
      responsive: { active: false, initial: null, current: null },
      sales: { active: false, initial: null, current: null },
    },
    paperConsents: [],
    canUserChangeResponsivePartner: false,
    initialResponsiveDealer: null,
    initialSalesDealer: null,
    editedItem: {
      slot_number: "",
      ce_number: "",
      dealer: null,
      crm_group: null,
      device: null,
      device_serial: null,
      delivery_date: null,
      delivery_date_to_customer: null,
      description: "",
      uuid: null,
      boat_items: [],
      documents: [],
      model_uuid: null,
      responsive_dealer: null,
      sales_dealer: null,
      responsive_dealer_uuid: "",
      sales_dealer_uuid: "",
      can_edit_paper_consents: false,
      editable_fields: ["*"],
      selected_profile_uuid: null,
    },
  }),

  watch: {
    "editedItem.responsive_dealer": {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {},
    },
  },
  computed: {
    ...mapGetters([
      "getDocumentLoadingStatus",
      "getItemLoadingStatus",
      "getMe",
      "canUseLicencedFeature",
      "createNewPartsHandler",
      "createNewDocumentsHandler",
    ]),
    canUserEdit() {
      let canUser = true;

      if (this.$route.name === "boatsEdit") {
        canUser = !!this.getMe.menu_access.boats.write_groups.find(
          (item) => item.uuid === this.editedItem.crm_group.uuid
        );
      }

      return canUser;
    },
    showImport() {
      return !!restrictions.canUseCrmFeature("can_use_boat_csv_import");
    },
    rules() {
      return restrictions.getBoatRules();
    },
    canSetCrmBoatTelematicsProfile() {
      return !!restrictions.canUseUserFeature(
        "can_set_crm_boat_telematics_profile"
      );
    },
  },
  created() {
    this.setInitialData();
    if (this.$route.name === "boatsEdit") {
      this.isEditView = true;

      let promises = [
        api.getBoatItems(this.$route.params.id),
        api.getBoat(this.$route.params.id),
      ];

      Promise.all(promises).then((values) => {
        this.$set(this.initialBoatItemsData, "data", values[0].data);

        this.model = values[1].data.model;

        this.editedItem = values[1].data;

        this.initialResponsiveDealer = this.editedItem.responsive_dealer;
        this.initialSalesDealer = this.editedItem.sales_dealer;

        this.editedItem.responsive_dealer_uuid =
          typeof this.editedItem.responsive_dealer.uuid !== "undefined"
            ? this.editedItem.responsive_dealer.uuid
            : "";

        this.editedItem.sales_dealer_uuid =
          typeof this.editedItem.sales_dealer.uuid !== "undefined"
            ? this.editedItem.sales_dealer.uuid
            : "";

        this.setCanUserChangeServicingPartner();

        if (
          this.editedItem.can_edit_paper_consents &&
          (typeof this.editedItem.sales_dealer.uuid !== "undefined" ||
            typeof this.editedItem.responsive_dealer.uuid !== "undefined")
        ) {
          this.loadPaperConsents();
        } else {
          this.editedItem.can_edit_paper_consents = false;
        }

        this.loading = false;
        this.editedItem.model_uuid = this.model.uuid;

        this.serialDocuments = this.editedItem.model.documents.map(
          (document) => {
            return { ...document, is_removable: false };
          }
        );
        this.customDocuments = this.editedItem.documents;
      });
    } else {
      this.loading = false;
    }
  },

  mounted() {
    this.$store.subscribe((mutation, state) => {
      if (
        mutation.type === "ITEMADDED" &&
        (state.item.from === "boatsEdit" || state.item.from === "boatsNew")
      ) {
        setTimeout(() => {
          this.$refs.boatItemsRef.addItemFromAutocomplete(
            mutation.payload.item
          );
        }, 0);
      }
      if (
        mutation.type === "DOCUMENTADDED" &&
        (state.document.from === "boatsEdit" ||
          state.document.from === "boatsNew")
      ) {
        this.addCustomDocuments(mutation.payload.document);
      }
    });
    if (this.$route.name !== "boatsEdit") {
      this.$watch("editedItem", this.formEdited, {
        deep: true,
      });
    }
  },

  updated() {
    if (this.$route.name === "boatsEdit") {
      this.$watch("editedItem", this.formEdited, {
        deep: true,
      });
    }
  },

  methods: {
    checkForProfileChange() {
      if (!this.canSetCrmBoatTelematicsProfile) {
        this.save();
        return;
      }
      if (!this.isEditView) {
        this.save();
        return;
      }

      if (
        this.editedItem.selected_profile == null &&
        this.editedItem.selected_profile_uuid == null
      ) {
        this.save();
        return;
      }

      if (
        this.editedItem.selected_profile == null &&
        this.editedItem.selected_profile_uuid != null
      ) {
        this.openConfirmProfileChangeDialog();
        return;
      }

      if (
        this.editedItem.selected_profile != null &&
        this.editedItem.selected_profile_uuid !==
          this.editedItem.selected_profile.uuid
      ) {
        this.openConfirmProfileChangeDialog();
        return;
      }

      this.save();
    },

    openConfirmProfileChangeDialog() {
      this.confirmProfileChangeDialog = {
        active: true,
        title: this.$t("builder.boats.addEdit.confirmProfileChange"),
        text: this.$t(
          "builder.boats.addEdit.thisCouldResetTheCurrentUsersSensorConfiguration"
        ),
      };
    },
    handleConfirmProfileChangeOutput(event) {
      this.confirmProfileChangeDialog = {
        ...this.confirmProfileChangeDialog,
        active: false,
      };
      if (event) {
        this.save();
      }
    },
    computeInitialDealerChangeString() {
      if (
        this.dealerChangeDialog.responsive.active &&
        this.dealerChangeDialog.sales.active
      ) {
        const responsiveName = this.dealerChangeDialog.responsive.initial
          ? this.dealerChangeDialog.responsive.initial.name
          : null;
        const salesName = this.dealerChangeDialog.sales.initial
          ? this.dealerChangeDialog.sales.initial.name
          : null;

        if (!responsiveName && !salesName) {
          return "";
        }

        if (responsiveName === salesName) {
          return responsiveName ? `(${responsiveName})` : "";
        } else {
          const names = [responsiveName, salesName].filter(Boolean).join(", ");
          return names ? `(${names})` : "";
        }
      } else {
        if (this.dealerChangeDialog.responsive.active) {
          const name = this.dealerChangeDialog.responsive.initial
            ? this.dealerChangeDialog.responsive.initial.name
            : null;
          return name ? `(${name})` : "";
        } else if (this.dealerChangeDialog.sales.active) {
          const name = this.dealerChangeDialog.sales.initial
            ? this.dealerChangeDialog.sales.initial.name
            : null;
          return name ? `(${name})` : "";
        }
      }
      return "";
    },
    computeCurrentDealerChangeString() {
      if (
        this.dealerChangeDialog.responsive.active &&
        this.dealerChangeDialog.sales.active
      ) {
        const responsiveName = this.dealerChangeDialog.responsive.initial
          ? this.dealerChangeDialog.responsive.initial.name
          : null;
        const salesName = this.dealerChangeDialog.sales.initial
          ? this.dealerChangeDialog.sales.initial.name
          : null;

        if (!responsiveName && !salesName) {
          return "";
        }

        if (responsiveName === salesName) {
          return responsiveName ? `(${responsiveName})` : "";
        } else {
          const names = [responsiveName, salesName].filter(Boolean).join(", ");
          return names ? `(${names})` : "";
        }
      } else {
        if (this.dealerChangeDialog.responsive.active) {
          const name =
            this.dealerChangeDialog.responsive.current &&
            this.dealerChangeDialog.responsive.current.name;
          return name ? `(${name})` : "";
        } else if (this.dealerChangeDialog.sales.active) {
          const name =
            this.dealerChangeDialog.sales.current &&
            this.dealerChangeDialog.responsive.current.name;
          return name ? `(${name})` : "";
        }
      }
      return "";
    },
    editableFields(input) {
      if (typeof this.editedItem.editable_fields === "undefined") return true;
      if (this.editedItem.editable_fields.length === 0) return true;
      if (this.editedItem.editable_fields.includes("*")) return false;

      return !this.editedItem.editable_fields.find(
        (element) => element === input
      );
    },
    setCanUserChangeServicingPartner() {
      this.canUserChangeResponsivePartner = !!this.getMe.dealerships.find(
        (group) => {
          if (group.sub_partners.length !== 0) {
            const subGroups = group.sub_partners
              .map((element) => element.uuid)
              .includes(this.editedItem.responsive_dealer_uuid);

            if (subGroups) {
              return true;
            }
          }

          return (
            group.uuid === this.editedItem.responsive_dealer_uuid &&
            (group.sub_partners.length !== 0 ||
              group.parent_partner_uuid !== null)
          );
        }
      );
    },

    handleDealerChange(dealer, action) {
      if (action === "SALES") {
        this.editedItem.sales_dealer = dealer;
        this.editedItem.sales_dealer_uuid = dealer.uuid;
        return;
      }

      if (action === "RESPONSIVE") {
        this.editedItem.responsive_dealer = dealer;
        this.editedItem.responsive_dealer_uuid = dealer.uuid;
        return;
      }

      console.error("DEALER CHANGE: Unknown dealer or action", dealer, action);
    },

    checkOwnedResources(changedPartnerUuids = []) {
      return api.checkOwnedResources(this.$route.params.id, {
        crm_partner_uuids: changedPartnerUuids,
      });
    },
    checkForDealerChange() {
      if (
        !this.isEditView ||
        typeof this.initialSalesDealer.uuid === "undefined" ||
        typeof this.initialResponsiveDealer.uuid === "undefined" ||
        (Object.keys(this.editedItem.sales_dealer).length === 0 &&
          Object.keys(this.editedItem.responsive_dealer).length === 0) ||
        (this.initialSalesDealer.uuid === this.editedItem.sales_dealer_uuid &&
          this.initialResponsiveDealer.uuid ===
            this.editedItem.responsive_dealer_uuid) ||
        (this.initialSalesDealer.uuid === this.initialResponsiveDealer.uuid &&
          (this.editedItem.responsive_dealer_uuid ===
            this.initialResponsiveDealer.uuid ||
            this.initialSalesDealer.uuid === this.initialResponsiveDealer.uuid))
      ) {
        this.checkForProfileChange();
        return;
      }

      if (
        this.initialResponsiveDealer.uuid !==
          this.editedItem.responsive_dealer_uuid &&
        this.initialResponsiveDealer.name !== ""
      ) {
        this.dealerChangeDialog = {
          ...this.dealerChangeDialog,
          responsive: {
            active: true,
            initial: this.initialResponsiveDealer,
            current: this.editedItem.responsive_dealer,
          },
        };
      }

      if (
        this.initialSalesDealer.uuid !== this.editedItem.sales_dealer_uuid &&
        this.initialSalesDealer.name !== ""
      ) {
        this.dealerChangeDialog = {
          ...this.dealerChangeDialog,
          sales: {
            active: true,
            initial: this.initialSalesDealer,
            current: this.editedItem.sales_dealer,
          },
        };
      }

      return new Promise((resolve, reject) => {
        let changedPartners = [
          this.dealerChangeDialog.sales.active
            ? this.dealerChangeDialog.sales.initial.uuid
            : null,
          this.dealerChangeDialog.responsive.active
            ? this.dealerChangeDialog.responsive.initial.uuid
            : null,
        ].filter((item, index, array) => {
          // NOTE: remove null items and duplicates
          return !!item && array.indexOf(item) === index;
        });

        if (changedPartners.length === 0) {
          console.error("CHECK FOR DEALER CHANGE: length is 0");
          reject();
        }

        this.checkOwnedResources(changedPartners).then((resp) => {
          this.dealerChangeDialog = {
            ...this.dealerChangeDialog,
            active: true,
            items: resp.data.crm_partner_resources.flatMap((item) => {
              return item.owned_items;
            }),
            documents: resp.data.crm_partner_resources.flatMap((item) => {
              return item.owned_documents;
            }),
          };
          resolve(resp.data.crm_partner_resources);
        });
      });
    },
    handleDealerChangeDialog(event) {
      if (!event) {
        this.$set(
          this.editedItem,
          "sales_dealer_uuid",
          this.initialSalesDealer.uuid
        );
        this.$set(this.editedItem, "sales_dealer", this.initialSalesDealer);
        this.forceUpdateSalesDealer++;

        this.$set(
          this.editedItem,
          "responsive_dealer_uuid",
          this.initialResponsiveDealer.uuid
        );
        this.$set(
          this.editedItem,
          "responsive_dealer",
          this.initialResponsiveDealer
        );
        this.forceUpdateResponsiveDealer++;
        this.resetDealerChangeDialog();
      } else if (event) {
        this.resetDealerChangeDialog();
        this.checkForProfileChange();
      }
    },

    resetDealerChangeDialog() {
      this.dealerChangeDialog = {
        active: false,
        title: "Confirm change of partner",
        documents: [],
        items: [],
        responsive: { active: false, initial: null, current: null },
        sales: { active: false, initial: null, current: null },
      };
    },
    setInitialData() {
      this.paperConsentsDialog = {
        active: false,
        title: this.$t("builder.boats.addEdit.confirmYourSelection"),
        text: this.$t(
          "builder.boats.addEdit.makeSureYourSelectionMatchesTheConsentsAvailableThisActionIsLoggedForAuditPurposes"
        ),
      };
      this.uploadDialog = {
        file: null,
        active: false,
        title: this.$t("builder.boats.addEdit.cancelInvite"),
        text: this.$t(
          "builder.boats.addEdit.areYouSureYouWantToCancelTheInvite"
        ),
      };
    },
    getRule(rule) {
      return this.rules.find((element) => element.rule === rule);
    },
    loadPaperConsents() {
      api.getPaperConsents(this.$route.params.id).then((resp) => {
        this.paperConsents = resp.data.map((element) => {
          return { ...element, active: !!element.date_accepted };
        });
        if (this.paperConsents.length === 0) {
          this.editedItem.can_edit_paper_consents = false;
        }
      });
    },
    savePaperConsents(event) {
      let data = this.paperConsents.map((consents) => {
        return {
          slug_name: consents.slug_name,
          date_accepted: consents.active ? new Date() : null,
          crm_partner_uuid: consents.crm_partner.uuid,
        };
      });
      api.setPaperConsents(this.$route.params.id, data).then((resp) => {
        snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
        this.paperConsentsDialog.active = false;
        this.isBoatEdited = false;
      });
    },
    openPaperConsentsDialog() {
      this.paperConsentsDialog.active = true;
    },
    handlePaperConsentsDialogOutput(event) {
      if (!event) {
        this.paperConsentsDialog.active = false;
        this.isBoatEdited = false;
        return;
      }

      this.savePaperConsents();
    },
    formEdited: function (newV, oldV) {
      this.isBoatEdited = true;
    },

    redirectRoute(event) {
      if (event) {
        this.$router.push(this.unsavedContent.route);
      }
      this.unsavedContent.active = false;
      this.unsavedContent.route = null;
    },

    importCSV() {
      this.isSelecting = true;
      window.addEventListener(
        "focus",
        () => {
          this.isSelecting = false;
        },
        { once: true }
      );
      this.$refs.uploader.click();
    },

    onFileChanged(e) {
      this.uploadDialog.file = e.target.files[0];
      this.uploadDialog.active = true;
    },

    handleCloseUploadFileDialog(items) {
      if (Array.isArray(items) && items.length !== 0) {
        this.$refs.boatItemsRef.setBoatItems(items, "external");
        snackbar.success(
          this.$t("utils.snackbar.success.itemsSuccessfullyImported")
        );
      }
      document.getElementById("csvFileImport").value = "";
      this.uploadDialog.file = null;
      this.uploadDialog.active = false;
    },

    addCustomDocuments(document) {
      this.customDocuments.unshift(document);
      this.forceUpdateCustomDocuments++;
    },

    deleteSerialDocuments(uuid) {
      const index = this.serialDocuments.findIndex((item) => {
        return item.uuid === item;
      });
      this.serialDocuments.splice(index, 1);
      this.forceUpdateSerialDocuments++;
    },

    deleteCustomDocuments(index) {
      this.customDocuments.splice(index, 1);
      this.forceUpdateCustomDocuments++;
    },

    openItemDialog() {
      this.$store.commit("ITEMPATH", this.$route.name);
      this.$store.commit("ITEMDIALOGSTATUS", true);
    },

    openDocumentDialog() {
      this.$store.commit("DOCUMENTPATH", this.$route.name);
      this.$store.commit("DOCUMENTDIALOGSTATUS", true);
    },

    boatItemTemplate(element) {
      return {
        warranty_months: element.warranty_months,
        warranty_start_date: null,
        extended_warranty_months: element.extended_warranty_months,
        serial_number: "",
        crm_item: element,
      };
    },

    loadNewBoatItems(model) {
      this.model = model;
      // this.initialBoatItemsData = {};
      this.$set(this.initialBoatItemsData, "data", {
        results: model.items,
        next: null,
      });

      this.serialDocuments = this.model.documents.map((document) => {
        return { ...document, is_removable: false };
      });
      this.reloadBoatList++;
      this.setModelSelectedProfile();
    },
    setModelSelectedProfile() {
      this.editedItem.selected_profile_uuid = this.model.selected_profile.uuid;
      this.profileSelectorKey++;
    },

    close(uuid) {
      if (typeof uuid !== "undefined") {
        api
          .getBoat(uuid, false)
          .then(() => {
            this.$router.push({
              name: "boatsDetails",
              params: { id: uuid },
            });
          })
          .catch(() => {
            if (this.dealerChangeDialog.triggered) {
              snackbar.success(
                this.$t(
                  "utils.snackbar.success.successfullyChangedServicingPartnerTheBoatIsNoLongerVisibleInYourAccount"
                )
              );
            } else {
              snackbar.error();
            }
            this.$router.push({
              name: "boats",
            });
          });
      } else {
        this.$router.push({
          name: "boats",
        });
      }
    },

    closePermissions(uuid) {
      this.isBoatEdited = false;
      this.close();
    },

    handleError(err) {
      this.loading = false;
    },

    save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        this.boatItems = this.boatItems.filter((item) => !item.special);
        this.editedItem.boat_items_update = [];
        this.editedItem.boat_items_create = [];
        this.editedItem.boat_items_delete = this.deletedBoatItems;

        if (
          this.editedItem.responsive_dealer_uuid === "" ||
          typeof this.editedItem.responsive_dealer_uuid === "undefined"
        ) {
          this.editedItem.responsive_dealer_uuid = null;
        }

        if (
          this.editedItem.sales_dealer_uuid === "" ||
          typeof this.editedItem.sales_dealer_uuid === "undefined"
        ) {
          this.editedItem.sales_dealer_uuid = null;
        }

        this.boatItems.forEach((element) => {
          element.boat_items
            .map((boatItem) => {
              if (
                typeof boatItem.action !== "undefined" &&
                boatItem.action === "updated"
              )
                return { crm_item_uuid: element.uuid, ...boatItem };
            })
            .filter((items) => {
              if (typeof items !== "undefined") {
                this.editedItem.boat_items_update.push(items);
              }
            });
        });

        this.boatItems.forEach((element) => {
          element.boat_items
            .map((boatItem) => {
              if (
                typeof boatItem.action !== "undefined" &&
                boatItem.action === "created"
              )
                return { crm_item_uuid: element.uuid, ...boatItem };
            })
            .filter((items) => {
              if (typeof items !== "undefined") {
                this.editedItem.boat_items_create.push(items);
              }
            });
        });

        this.editedItem.boat_items_create.forEach((items) => {
          delete items.uuid;
        });

        delete this.editedItem.boat_items;

        this.editedItem.documents = this.customDocuments;
        this.editedItem.document_uuids = this.editedItem.documents.map(
          (el) => el.uuid
        );

        if (typeof this.editedItem.device_serial === "undefined") {
          this.editedItem.device_serial = null;
          delete this.editedItem.device;
        }

        const handleSave = (resp) => {
          this.isBoatEdited = false;
          snackbar.success(this.$t("utils.snackbar.success.successfullySaved"));
          this.close(resp.data.uuid);
        };

        if (
          this.editedItem.retrofit_status == "CONFIRMED" ||
          !this.canSetCrmBoatTelematicsProfile
        ) {
          delete this.editedItem.selected_profile_uuid;
        }

        if (!this.isEditView) {
          api
            .createBoat(this.editedItem)
            .then((resp) => {
              handleSave(resp);
            })
            .catch((err) => {
              this.handleError(err.response);
            });
        } else {
          if (!this.canUserEdit) {
            api
              .updateBoatAsDealer(this.editedItem.uuid, this.editedItem)
              .then((resp) => {
                handleSave(resp);
              })
              .catch((err) => {
                this.handleError(err.response);
              });
          } else {
            api
              .updateBoat(this.editedItem.uuid, this.editedItem)
              .then((resp) => {
                handleSave(resp);
              })
              .catch((err) => {
                this.handleError(err.response);
              });
          }
        }
      } else {
        snackbar.error();
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    if (!this.isBoatEdited) {
      next();
    } else {
      if (this.unsavedContent.route) {
        next();
      } else {
        this.unsavedContent.active = true;
        this.unsavedContent.route = to;
        next(false);
      }
    }
  },
};
</script>
<style scoped>
.v-card__text,
.v-card__title {
  word-break: normal;
}
</style>
